import React, { useState } from "react";
import * as XLSX from "xlsx";
import { toast, ToastContainer } from "react-toastify";
import useContacts from "../../../services/chat/useContacts";

const C = {
  green: "#25d366",
  greenDk: "#128c7e",
  bg: "#f0f2f5",
  white: "#ffffff",
  border: "#d1d5db",
  text: "#111b21",
  sub: "#667781",
  muted: "#8696a0",
  red: "#e53e3e",
  blue: "#1877f2",
  blueBg: "#f0f4ff",
};

const inp = {
  width: "100%",
  padding: "9px 12px",
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  fontSize: 13.5,
  color: C.text,
  outline: "none",
  boxSizing: "border-box",
  background: C.white,
};

const ContactAdd = ({ userID }) => {
  const { addContact, uploadBulkContacts } = useContacts();

  const [selectedMethod, setSelectedMethod] = useState("1");
  const [contactName, setContactName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [gender, setGender] = useState("");
  const [tags, setTags] = useState([""]);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // ── Tags ──
  const addTag = () => setTags([...tags, ""]);
  const removeTag = (i) => {
    const u = [...tags];
    u.splice(i, 1);
    setTags(u);
  };
  const handleTagChange = (i, v) => {
    const u = [...tags];
    u[i] = v;
    setTags(u);
  };

  // ── Add single contact ──
  const handleAddContact = async () => {
  if (!contactName) {
    toast.error("Please enter name");
    return;
  }

  if (!contactNumber || contactNumber.length !== 10) {
    toast.error("Enter valid mobile number");
    return;
  }

  try {
    setLoading(true);

    const res = await addContact({
      contactNumber,
      contactEmail,
      contactName,
      gender,
      tags,
    });

    console.log("Add Contact Response:", res);

    if (res?.responseCode === "00") {
      toast.success(res.responseMessage || " Contact added successfully");

      // reset form
      setContactName("");
      setContactNumber("");
      setContactEmail("");
      setGender("");
      setTags([""]);
    } else {
      toast.error(res?.responseMessage || " Failed to add contact");
    }
  } catch (err) {
    console.error(err);
    toast.error(" Something went wrong");
  } finally {
    setLoading(false);
  }
};

  // ── Process file ──
  const processFile = (selectedFile) => {
    if (!selectedFile) return;
    if (
      !selectedFile.name.endsWith(".xlsx") &&
      !selectedFile.name.endsWith(".csv")
    ) {
      toast.error("Please upload .xlsx or .csv file");
      return;
    }
    setFile(selectedFile);
  };

  // ── Bulk upload ──
  const handleFileUpload = () => {
    if (!file) {
      toast.error("Upload Excel file");
      return;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
      const json = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
      const formatted = json.map((item) => ({
        userid: userID,
        contactNumber: item.contactNumber,
        contactEmail: item.contactEmail,
        contactName: item.contactName,
        gender: item.gender,
        tags: item.tags ? item.tags.split(",").map((t) => t.trim()) : [],
      }));
      try {
        const res = await uploadBulkContacts(formatted);
        if (res.responseCode === "00") {
          toast.success(res.responseMessage);
          // fetchChatContacts();
          setFile(null);
        } else toast.warning(res.responseMessage);
      } catch {
        toast.error("Upload failed");
      }
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);
  };

  // ── Download sample CSV ──
  const handleDownloadSample = () => {
    const headers = [
      "contactName",
      "contactNumber",
      "contactEmail",
      "gender",
      "tags",
    ];
    const sample = [
      "John Doe",
      "9876543210",
      "john@example.com",
      "Male",
      "vip,customer",
    ];
    const csv = [headers.join(","), sample.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_contacts.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <ToastContainer />

    <div className="montserrat-font-1"
      style={{
        background: C.white,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "22px 28px 14px",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <p
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: C.text,
            margin: "0 0 6px",
          }}
        >
          Add Customer Contacts
        </p>
        <p style={{ fontSize: 13, color: C.sub, margin: 0, lineHeight: 1.6 }}>
          Please provide the contact number you wish to add. This will help us
          keep in touch with you for updates and important information.
        </p>
      </div>

      {/* Form */}
      <div style={{ padding: "22px 28px", flex: 1 }}>
        {/* Radio */}
        <div style={{ display: "flex", gap: 28, marginBottom: 22 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              color: C.text,
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              checked={selectedMethod === "1"}
              onChange={() => setSelectedMethod("1")}
              style={{ accentColor: C.green, width: 16, height: 16 }}
            />
            One By One
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              color: C.text,
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              checked={selectedMethod === "2"}
              onChange={() => setSelectedMethod("2")}
              style={{ accentColor: C.green, width: 16, height: 16 }}
            />
            Bulk Upload
          </label>
        </div>

        {/* ══ ONE BY ONE ══ */}
        {selectedMethod === "1" && (
          <>
            {/* Name + Mobile — same row */}
            <div className="montserrat-font-1"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: 16,
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    marginBottom: 6,
                  }}
                >
                  Name <span style={{ color: C.red }}>*</span>
                </label>
                <form autoComplete="off">
                  <input
                    placeholder="Enter contact name."
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    onFocus={(e) => (e.target.style.borderColor = C.green)}
                    onBlur={(e) => (e.target.style.borderColor = C.border)}
                    style={inp}
                  />
                </form>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    marginBottom: 6,
                  }}
                >
                  Mobile <span style={{ color: C.red }}>*</span>
                </label>
                <form autoComplete="off">
                  <input
                    placeholder="Enter mobile number."
                    value={contactNumber}
                    onChange={(e) =>
                      setContactNumber(e.target.value.replace(/\D/g, ""))
                    }
                    onFocus={(e) => (e.target.style.borderColor = C.green)}
                    onBlur={(e) => (e.target.style.borderColor = C.border)}
                    style={inp}
                  />
                </form>
              </div>
            </div>

            {/* Email + Gender — same row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
                marginBottom: 16,
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    marginBottom: 6,
                  }}
                >
                  E-mail
                </label>
                <form autoComplete="off">
                  <input
                    placeholder="Enter e-mail id."
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    onFocus={(e) => (e.target.style.borderColor = C.green)}
                    onBlur={(e) => (e.target.style.borderColor = C.border)}
                    style={inp}
                  />
                </form>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: C.text,
                    marginBottom: 6,
                  }}
                >
                  Gender
                </label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  style={{
                    ...inp,
                    color: gender ? C.text : C.muted,
                    cursor: "pointer",
                  }}
                >
                  <option value="">Select Gender</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>
            </div>

            {/* Tags — label + inputs in same grid row alignment */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.text,
                  marginBottom: 8,
                }}
              >
                Tags
              </label>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "16px",
                  alignItems: "start",
                }}
              >
                {tags.map((tag, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <input
                      placeholder="Enter Tag Name"
                      value={tag}
                      onChange={(e) => handleTagChange(i, e.target.value)}
                      onFocus={(e) => (e.target.style.borderColor = C.green)}
                      onBlur={(e) => (e.target.style.borderColor = C.border)}
                      style={{ ...inp }}
                    />
                    <button
                      onClick={() => removeTag(i)}
                      style={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        border: `1px solid ${C.border}`,
                        background: C.bg,
                        color: C.muted,
                        fontSize: 14,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {/* Add More Tag always appears in next grid slot */}
                <div
                  style={{ display: "flex", alignItems: "center", height: 42 }}
                >
                  <button
                    onClick={addTag}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                      background: C.white,
                      color: C.sub,
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    + Add More Tag
                  </button>
                </div>
              </div>
            </div>

            {/* Save */}
            <button
              onClick={handleAddContact}
              style={{
                padding: "11px 36px",
                borderRadius: 9,
                border: "none",
                background: `linear-gradient(135deg,${C.green},${C.greenDk})`,
                color: C.white,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 3px 14px rgba(37,211,102,.35)",
              }}
            >
              Save
            </button>
          </>
        )}

        {/* ══ BULK UPLOAD ══ */}
        {selectedMethod === "2" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Drag & Drop upload area */}
            <label
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                processFile(e.dataTransfer.files[0]);
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                padding: "48px 24px",
                border: `1.5px dashed ${dragOver ? C.blue : "#93c5fd"}`,
                borderRadius: 12,
                cursor: "pointer",
                background: dragOver ? "#eff6ff" : C.blueBg,
                transition: "all .15s",
              }}
            >
              {/* Upload icon */}
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke={C.blue}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
              <span style={{ fontSize: 14, fontWeight: 600, color: C.blue }}>
                {file ? file.name : "Click to upload Excel file"}
              </span>
              <input
                type="file"
                accept=".xlsx,.csv"
                style={{ display: "none" }}
                onChange={(e) => processFile(e.target.files[0])}
              />
            </label>

            {/* Bottom row: hint + download sample */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 13, color: C.sub }}>
                You can download a CSV sample for your reference
              </span>
              <button
                onClick={handleDownloadSample}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: `1px solid ${C.border}`,
                  background: C.white,
                  color: C.sub,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download Sample Excel
              </button>
            </div>

            {/* Save / Upload button */}
            <button
              onClick={handleFileUpload}
              disabled={loading}
              style={{
                alignSelf: "flex-start",
                padding: "11px 36px",
                borderRadius: 9,
                border: "none",
                background: `linear-gradient(135deg,${C.green},${C.greenDk})`,
                color: C.white,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                opacity: loading ? 0.7 : 1,
                boxShadow: "0 3px 14px rgba(37,211,102,.35)",
              }}
            >
              {loading ? "Uploading…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ContactAdd;
